import cn from 'classnames'
import React, { KeyboardEvent, ReactElement } from 'react'

import style from './Switch.style'

type SwitchProps = {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  /** Accessible label — required since the switch renders no visible text. */
  ariaLabel: string
  disabled?: boolean
  className?: string
  'data-testid'?: string
}

/**
 * Small on/off toggle. Renders a native `<button role="switch">`, so it is
 * keyboard operable (Space/Enter) and exposes its state via `aria-checked`.
 * Consumers pass an already-translated `ariaLabel` — the switch renders no
 * visible text of its own. Transitions are disabled under
 * `prefers-reduced-motion`.
 */
export default function Switch(props: SwitchProps): ReactElement {
  const {
    checked,
    onCheckedChange,
    ariaLabel,
    disabled = false,
    className = '',
    'data-testid': dataTestId,
  } = props

  const toggle = (): void => {
    if (!disabled) {
      onCheckedChange(!checked)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      toggle()
    }
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      className={cn('Switch', { checked }, className)}
      css={style}
      onClick={toggle}
      onKeyDown={handleKeyDown}
      data-testid={dataTestId}
    >
      <span className="Switch-thumb" aria-hidden="true" />
    </button>
  )
}
